import { useEffect, useState } from "react";
import { Mail, Phone, MessageCircle, MapPin, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { endpoints, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUPPORT_WHATSAPP, waSupportLink } from "@/lib/support";
import { LegalLayout } from "@/components/LegalLayout";
import { SocialLinks } from "@/components/Footer";

const DEFAULTS = { email: "support@bbkigali.com", phone: "+250 798 875 272", whatsapp: SUPPORT_WHATSAPP, address: "Kigali, Rwanda" };

const Row = ({ icon: Icon, label, children, testid }) => (
  <div className="flex items-start gap-3" data-testid={testid}>
    <span className="w-10 h-10 rounded-xl bg-[#E11D2A]/15 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#EF4444]" aria-hidden="true" /></span>
    <div><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</div><div className="text-white">{children}</div></div>
  </div>
);

const input = "w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white text-sm outline-none focus:border-[#E11D2A]/60";

export default function Contact() {
  const { user } = useAuth();
  const [info, setInfo] = useState(DEFAULTS);
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    endpoints.settings().then(({ data }) => setInfo({
      email: data?.contactEmail || DEFAULTS.email, phone: data?.contactPhone || DEFAULTS.phone,
      whatsapp: (data?.contactWhatsapp || DEFAULTS.whatsapp).replace(/\D/g, ""), address: data?.contactAddress || DEFAULTS.address,
    })).catch(() => {});
  }, []);
  useEffect(() => { if (user) setF((p) => ({ ...p, name: p.name || user.displayName || "", phone: p.phone || user.phone || "", email: p.email || user.email || "" })); }, [user]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.message.trim()) return toast.error("Please enter your name and message.");
    if (!f.phone.trim() && !f.email.trim()) return toast.error("Please enter a phone number or email so we can reply.");
    setBusy(true);
    try { await endpoints.contact(f); setSent(true); toast.success("Message sent — our team will reply soon."); }
    catch (err) { toast.error(apiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const wa = info.whatsapp === SUPPORT_WHATSAPP ? waSupportLink() : `https://wa.me/${info.whatsapp}`;

  return (
    <LegalLayout icon={MessageCircle} title="Contact us" subtitle="We're here to help — usually within one business day." testid="contact-page">
      <div className="grid md:grid-cols-5 gap-8">
        <div className="md:col-span-2 space-y-5">
          <Row icon={MessageCircle} label="WhatsApp support" testid="contact-whatsapp">
            <a href={wa} target="_blank" rel="noopener noreferrer" className="hover:underline">+{info.whatsapp}</a>
          </Row>
          <Row icon={Mail} label="Email" testid="contact-email"><a href={`mailto:${info.email}`} className="hover:underline">{info.email}</a></Row>
          <Row icon={Phone} label="Phone" testid="contact-phone"><a href={`tel:${info.phone.replace(/\s/g, "")}`} className="hover:underline">{info.phone}</a></Row>
          <Row icon={MapPin} label="Address" testid="contact-address">{info.address}</Row>
          <div className="pt-2"><div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Follow us</div><SocialLinks prefix="contact" /></div>
        </div>
        <div className="md:col-span-3 rounded-2xl bg-[#121620] border border-[#273042] p-6">
          {sent ? (
            <div className="text-center py-10" data-testid="contact-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" aria-hidden="true" />
              <div className="text-white font-bold text-lg">Thank you! Your message was sent.</div>
              <p className="text-slate-300 text-sm mt-1">Our support team will reply by WhatsApp, SMS or email.</p>
              <button onClick={() => { setSent(false); setF({ ...f, message: "" }); }} className="mt-5 text-sm text-[#EF4444] font-semibold hover:underline" data-testid="contact-send-another">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <h2 className="text-white font-bold text-lg">Send us a message</h2>
              <label className="block"><span className="text-sm text-slate-200">Your name</span>
                <input value={f.name} onChange={set("name")} className={`${input} mt-1.5`} data-testid="contact-name-input" maxLength={80} required /></label>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block"><span className="text-sm text-slate-200">Phone (with country code)</span>
                  <input value={f.phone} onChange={set("phone")} placeholder="+250…" className={`${input} mt-1.5`} data-testid="contact-phone-input" inputMode="tel" /></label>
                <label className="block"><span className="text-sm text-slate-200">Email</span>
                  <input type="email" value={f.email} onChange={set("email")} className={`${input} mt-1.5`} data-testid="contact-email-input" /></label>
              </div>
              <label className="block"><span className="text-sm text-slate-200">Message</span>
                <textarea value={f.message} onChange={set("message")} rows={5} maxLength={2000} required data-testid="contact-message-input"
                  className="mt-1.5 w-full rounded-xl bg-[#0A0C10] border border-[#273042] p-4 text-white text-sm outline-none focus:border-[#E11D2A]/60" /></label>
              <button type="submit" disabled={busy} data-testid="contact-submit-btn"
                className="h-11 px-6 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white font-bold inline-flex items-center gap-2 disabled:opacity-60 transition-colors">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Send className="w-4 h-4" aria-hidden="true" />} Send message
              </button>
            </form>
          )}
        </div>
      </div>
    </LegalLayout>
  );
}
